import { getRealtimeKitLanguage } from './RealtimeKitLocaleHelper';

const DESIGN_TOKENS = {
  theme: 'dark',
  borderRadius: 'rounded',
  colors: {
    brand: {
      300: '#6BA8F8',
      400: '#4A94F7',
      500: '#2781F6',
      600: '#1F68C5',
      700: '#174F94',
    },
  },
};

let customElementsReady;

const ensureCustomElements = async () => {
  if (!customElementsReady) {
    customElementsReady = import('@cloudflare/realtimekit-ui/loader').then(
      ({ defineCustomElements }) => defineCustomElements()
    );
  }
  await customElementsReady;
};

const enableMeetingMedia = async meeting => {
  const [videoResult, audioResult] = await Promise.allSettled([
    meeting.self.enableVideo(),
    meeting.self.enableAudio(),
  ]);

  if (videoResult.status === 'rejected') {
    // Permission denied / device missing — meeting still mounts; user can retry in UI.
    // eslint-disable-next-line no-console
    console.warn(
      '[RealtimeKit] Camera could not be enabled',
      videoResult.reason
    );
  }
  if (audioResult.status === 'rejected') {
    // eslint-disable-next-line no-console
    console.warn(
      '[RealtimeKit] Microphone could not be enabled',
      audioResult.reason
    );
  }
};

/**
 * Mounts RealtimeKit UI into `container` and joins without the setup screen.
 * Returns the meeting client so callers can leave/cleanup later.
 */
export const mountRealtimeKitMeeting = async (
  container,
  authToken,
  { locale } = {}
) => {
  if (!container || !authToken) {
    throw new Error('RealtimeKit meeting requires a container and authToken');
  }

  const [{ default: RealtimeKitClient }, { provideRtkDesignSystem }] =
    await Promise.all([
      import('@cloudflare/realtimekit'),
      import('@cloudflare/realtimekit-ui'),
      ensureCustomElements(),
    ]);

  const meeting = await RealtimeKitClient.init({
    authToken,
    defaults: { video: true, audio: true },
  });

  await enableMeetingMedia(meeting);

  container.replaceChildren();

  const meetingEl = document.createElement('rtk-meeting');
  meetingEl.style.width = '100%';
  meetingEl.style.height = '100%';
  container.appendChild(meetingEl);

  // Set after upgrade/connect so Stencil keeps boolean props (avoid setup screen).
  // leaveOnUnmount stays false — callers leave explicitly before tearing down the DOM
  // to avoid a second leave() against an already-closing WebSocket.
  meetingEl.showSetupScreen = false;
  meetingEl.leaveOnUnmount = false;
  meetingEl.t = getRealtimeKitLanguage(locale);
  provideRtkDesignSystem(container, DESIGN_TOKENS);
  meetingEl.meeting = meeting;

  return meeting;
};

export const leaveRealtimeKitMeeting = async meeting => {
  if (!meeting?.self?.roomJoined) return;

  try {
    await meeting.leave();
  } catch {
    // Meeting may already be left / torn down.
  }
};
